import { useState, useEffect, useRef } from 'react';
import Sidebar from './Sidebar';
import HinglishTextarea from './HinglishTextarea';
import {
  getVettingNext,
  getVettingClip,
  patchVettingClip,
  completeVettingClip,
  getVettingTodayStats,
} from '../services/api';

// detail is a string, {message, missing} on 400, or an array on 422.
const errorMessage = (e) => {
  const d = e.response?.data?.detail;
  if (!d) return e.message;
  if (typeof d === 'string') return d;
  return d.missing ? `${d.message}: ${d.missing.join(', ')}` : JSON.stringify(d);
};

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const get = (doc, path) => path.split('.').reduce((o, k) => o?.[k], doc);

// Turns the draft ({'vet.language': 'hindi', transcript: '…'}) into a PATCH body of fields that differ from the server doc.
const diff = (draft, doc) => {
  const out = {};
  for (const [path, v] of Object.entries(draft)) {
    if (same(v, get(doc, path))) continue;
    const [section, key] = path.split('.');
    if (key) (out[section] ||= {})[key] = v;
    else out[section] = v;
  }
  return out;
};

const show = (v) => {
  if (v == null) return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
};

const YES_NO = [true, false];

// [key, label, options]
const VET_FIELDS = [
  ['language', 'Language', ['hindi', 'english', 'hinglish', 'other']],
];
const LISTEN_FIELDS = [
  ['mispronunciation', 'Mispronunciation'],
  ['partial_word', 'Partial word'],
];
// Everything except the mispronunciation note must be answered before Submit.
const REQUIRED = [
  ...VET_FIELDS.map(([k]) => `vet.${k}`),
  ...LISTEN_FIELDS.map(([k]) => `listen.${k}`),
];

const LABELS = Object.fromEntries([
  ...VET_FIELDS.map(([k, label]) => [`vet.${k}`, label]),
  ...LISTEN_FIELDS.map(([k, label]) => [`listen.${k}`, label]),
]);

const MUTED = 'var(--text-muted,#888)';
const BORDER = '1px solid var(--border)';
const ACCENT_BTN = {
  background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8,
  padding: '10px 20px', fontWeight: 700, fontSize: 14,
};
const GHOST_BTN = {
  background: 'transparent', color: 'var(--text)', border: BORDER, borderRadius: 8,
  padding: '6px 12px', fontWeight: 600, fontSize: 13,
};
const chip = (on) => ({
  background: on ? 'var(--accent)' : 'transparent', color: on ? '#fff' : 'var(--text)',
  border: BORDER, borderRadius: 6, padding: '4px 12px', fontSize: 13,
});
const dim = (disabled) => ({ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1 });
const SECTION = { fontSize: 13, fontWeight: 700, margin: '16px 0 6px', color: MUTED, textTransform: 'uppercase' };
const ROW = { display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0', borderBottom: BORDER };

function Control({ options, value, onChange, disabled }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {options.map((o) => (
        <button key={String(o)} disabled={disabled} onClick={() => onChange(o)} style={{ ...chip(value === o), ...dim(disabled) }}>
          {show(o)}
        </button>
      ))}
    </div>
  );
}

export default function Val4200Vetting({ user, onPageChange }) {
  const [doc, setDoc]         = useState(null);   // server copy of the clip being vetted
  const [draft, setDraft]     = useState({});     // unsaved edits keyed by path
  const [busy, setBusy]       = useState(true);
  const [error, setError]     = useState(null);
  const [stats, setStats]     = useState(null);   // today's completed clips per reviewer
  const audioRetried          = useRef(null);

  const changes   = doc ? diff(draft, doc) : {};
  const dirty     = Object.keys(changes).length > 0;
  const readOnly  = doc?.status === 'completed';
  const val       = (path) => (path in draft ? draft[path] : get(doc, path));
  const set       = (path, v) => setDraft((d) => ({ ...d, [path]: v }));
  const missing   = doc ? REQUIRED.filter((p) => val(p) == null) : [];

  // Each response carries a fresh presigned audio_url; keep the current one so Save doesn't restart playback.
  const applyDoc = (next) => setDoc((d) => ({ audio_url: d?.audio_url, ...next }));

  // 409 = already completed, so reload the row and render it read-only.
  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try { await fn(); } catch (e) {
      setError(errorMessage(e));
      if (e.response?.status === 409 && doc) {
        try { setDoc(await getVettingClip(doc.row)); setDraft({}); } catch { /* keep the first error */ }
      }
    }
    setBusy(false);
  };

  const showNext = async () => {
    setDoc(null); // never leave a finished clip on screen if /next fails
    const next = await getVettingNext();
    setDoc(next);
    // Overlapping speech, mispronunciation and partial word start at No; the reviewer clicks Yes to change them.
    const defaults = {};
    if (next && next.status !== 'completed') {
      if (next.existing?.overlapping_speech) defaults['existing.overlapping_speech'] = null;
      for (const [k] of LISTEN_FIELDS) {
        if (next.listen?.[k] == null) defaults[`listen.${k}`] = false;
      }
    }
    setDraft(defaults);
  };
  // The panel is secondary, so a failed fetch just leaves the last numbers up.
  const loadStats = () => getVettingTodayStats().then(setStats).catch(() => {});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { run(showNext); loadStats(); }, []);

  const saveDraft = async () => {
    if (!dirty) return;
    applyDoc(await patchVettingClip(doc.row, changes));
    setDraft({});
  };

  const complete = () => run(async () => {
    await saveDraft();
    await completeVettingClip(doc.row);
    loadStats();
    await showNext(); // backend: call /next after each successful complete
  });

  // Presigned URLs expire after an hour: refetch once per clip when the audio fails.
  const onAudioError = async () => {
    if (audioRetried.current === doc.row) return;
    audioRetried.current = doc.row;
    try {
      const fresh = await getVettingClip(doc.row);
      setDoc((d) => ({ ...d, audio_url: fresh.audio_url }));
    } catch (e) { setError(errorMessage(e)); }
  };

  const annotated = doc?.transcript_status === 'annotated';

  return (
    <div className="workspace">
      <Sidebar
        user={user}
        currentPage="val4200-vetting"
        onPageChange={onPageChange}
        title={user?.name || user?.email || 'Reviewer'}
        subtitle="STT Vetting"
      />
      <main className="annotator" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div className="annotator-header" style={{ flexShrink: 0 }}>
          <h1 style={{ margin: 0 }}>STT Vetting</h1>
        </div>

        {error && <div style={{ color: '#ef4444', padding: '8px 16px' }}>{error}</div>}

        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Editor */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
            {busy && !doc && <div>Loading…</div>}
            {!busy && !doc && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 12 }}>
                {!error && <div>All done. No pending clips left.</div>}
                <button onClick={() => run(showNext)} style={{ ...ACCENT_BTN, cursor: 'pointer' }}>{error ? 'Retry' : 'Check again'}</button>
              </div>
            )}

            {doc && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <strong>Row {doc.row}</strong>
                  <span style={chip(annotated)}>{annotated ? '✎ Annotated by you' : 'Needs check'}</span>
                  <span style={{ color: MUTED, fontSize: 13 }}>
                    {readOnly ? `Completed ${doc.completed_at || ''} (read-only)` : 'Pending'}
                  </span>
                </div>

                <audio
                  key={doc.row}
                  src={doc.audio_url}
                  controls
                  preload="metadata"
                  onError={onAudioError}
                  style={{ width: '100%' }}
                />

                <div style={SECTION}>Transcript</div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: MUTED }}>Working transcript</div>
                    <HinglishTextarea
                      lang="hi"
                      rows={5}
                      disabled={readOnly}
                      value={val('transcript') ?? ''}
                      onChange={(e) => set('transcript', e.target.value)}
                      style={{ width: '100%', boxSizing: 'border-box', fontSize: 16 }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: MUTED }}>Original</div>
                    <div style={{ whiteSpace: 'pre-wrap', fontSize: 16, padding: 6, border: BORDER, borderRadius: 4, minHeight: 100 }}>
                      {doc.original_transcript}
                    </div>
                  </div>
                </div>

                <div style={SECTION}>Existing marks</div>
                <div style={ROW}>
                  <div style={{ width: 130, fontWeight: 600 }}>Usable</div>
                  <Control options={['usable', 'not_usable']} value={val('existing.usable')} disabled={readOnly} onChange={(v) => set('existing.usable', v)} />
                </div>
                <div style={ROW}>
                  <div style={{ width: 130, fontWeight: 600 }}>Speaker</div>
                  <Control options={['child', 'adult', 'both', 'doro', 'noise', 'noise_speech', 'noise_speech_child', 'noise_speech_adult', 'noise_speech_both']} value={val('existing.speaker')} disabled={readOnly} onChange={(v) => set('existing.speaker', v)} />
                </div>
                <div style={ROW}>
                  <div style={{ width: 130, fontWeight: 600 }}>Overlapping speech</div>
                  {/* Backend stores 'yes' or null, so null reads as No. */}
                  <Control options={YES_NO} value={val('existing.overlapping_speech') === 'yes'} disabled={readOnly} onChange={(v) => set('existing.overlapping_speech', v ? 'yes' : null)} />
                </div>

                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 300 }}>
                    <div style={SECTION}>Labels</div>
                    {VET_FIELDS.map(([k, label, options]) => (
                      <div key={k} style={ROW}>
                        <div style={{ width: 130, fontWeight: 600 }}>{label}</div>
                        <Control options={options} value={val(`vet.${k}`)} disabled={readOnly} onChange={(v) => set(`vet.${k}`, v)} />
                      </div>
                    ))}
                  </div>

                  <div style={{ flex: 1, minWidth: 300 }}>
                    <div style={SECTION}>Listening labels</div>
                    {LISTEN_FIELDS.map(([k, label]) => (
                      <div key={k} style={ROW}>
                        <div style={{ width: 130, fontWeight: 600 }}>{label}</div>
                        <Control options={YES_NO} value={val(`listen.${k}`)} disabled={readOnly} onChange={(v) => set(`listen.${k}`, v)} />
                        {k === 'mispronunciation' && val('listen.mispronunciation') === true && (
                          <input
                            placeholder="Which word / note"
                            disabled={readOnly}
                            value={val('listen.mispronunciation_note') ?? ''}
                            onChange={(e) => set('listen.mispronunciation_note', e.target.value)}
                            style={{ flex: 1 }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {!readOnly && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button disabled={!dirty || busy} onClick={() => run(saveDraft)} style={{ ...GHOST_BTN, padding: '10px 20px', ...dim(!dirty || busy) }}>
                        Save
                      </button>
                      <button disabled={missing.length > 0 || busy} onClick={complete} style={{ ...ACCENT_BTN, ...dim(missing.length > 0 || busy) }}>
                        {busy ? 'Submitting…' : 'Submit'}
                      </button>
                    </div>
                    {missing.length > 0 && (
                      <div style={{ fontSize: 12, color: MUTED, marginTop: 6 }}>Still missing: {missing.map((p) => LABELS[p]).join(', ')}</div>
                    )}
                  </div>
                )}
              </>
            )}

            {stats && (
              <>
                {/* The backend sends everyone to admins and only the caller's own row to everyone else. */}
                <div style={SECTION}>{user?.is_admin ? 'Completed today' : 'You completed today'} ({stats.date}) · {stats.total}</div>
                {stats.users.length === 0 && <div style={{ fontSize: 13, color: MUTED }}>No clips completed yet.</div>}
                {stats.users.map((u) => (
                  <div key={u.email} style={{ ...ROW, justifyContent: 'space-between' }}>
                    <span>{u.email}</span>
                    <strong>{u.count}</strong>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
